import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import { COMPANY, NAV, SERVICES } from "../data/site";
import { Button } from "./Button";
import { SiteSearch } from "./SiteSearch";
import { ThemeToggle } from "./ThemeToggle";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [itOpen, setItOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setItOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-silver/30 shadow-sm"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
            <img src={COMPANY.logo} alt="Silvertree Solutions logo" className="h-12 w-auto" />
            <img
              src={COMPANY.wordmark}
              alt="Silvertree Solutions"
              className="h-7 sm:h-9 w-auto"
              data-testid="header-wordmark"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <div
              className="relative"
              onMouseEnter={() => setItOpen(true)}
              onMouseLeave={() => setItOpen(false)}
            >
              <NavLink
                to="/business-it-support"
                data-testid="nav-it-support"
                className="flex items-center gap-1 text-sm font-medium text-charcoal hover:text-forest transition-colors py-2"
              >
                IT Support <ChevronDown className="h-4 w-4" />
              </NavLink>
              {itOpen && (
                <div className="absolute top-full left-0 w-64 bg-white border border-silver/30 rounded-lg shadow-xl p-4 space-y-1">
                  {[
                    { label: "Business IT Support", to: "/business-it-support" },
                    { label: "Silvertree Assist", to: "/it-support/assist" },
                    { label: "Silvertree Managed", to: "/it-support/managed" },
                    { label: "Silvertree Secure", to: "/it-support/secure" },
                    { label: "Switch IT Providers", to: "/switch-it-provider" },
                  ].map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-dropdown-${l.to.replace(/\//g, "-").replace(/^-/, "")}`}
                      className="block px-3 py-2.5 rounded-md text-sm text-charcoal hover:bg-forest/5 hover:text-forest transition-colors"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <NavLink
                to="/services"
                data-testid="nav-services"
                className="flex items-center gap-1 text-sm font-medium text-charcoal hover:text-forest transition-colors py-2"
              >
                Services <ChevronDown className="h-4 w-4" />
              </NavLink>
              {servicesOpen && (
                <div className="absolute top-full left-0 w-[600px] bg-white border border-silver/30 rounded-lg shadow-xl p-4 grid grid-cols-2 gap-1">
                  <Link
                    to="/technology-health-snapshot"
                    data-testid="nav-dropdown-technology-health-snapshot"
                    className="px-3 py-2.5 rounded-md text-sm text-forest hover:bg-forest/5 transition-colors"
                  >
                    Technology Health Assessment
                  </Link>
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-dropdown-${s.slug}`}
                      className="px-3 py-2.5 rounded-md text-sm text-charcoal hover:bg-forest/5 hover:text-forest transition-colors"
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {NAV.filter((n) => !["Services", "Contact", "IT Support"].includes(n.label)).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors py-2 ${
                    isActive ? "text-forest" : "text-charcoal hover:text-forest"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <ThemeToggle className="text-charcoal/70 hover:text-forest hover:bg-forest/5" />
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search the site"
              data-testid="header-search-button"
              className="p-2 rounded-md text-charcoal/70 hover:text-forest hover:bg-forest/5 transition-colors"
            >
              <Search className="h-5 w-5" strokeWidth={1.75} />
            </button>
            <Button to="/contact" data-testid="header-cta" size="md" className="ml-2">
              Start a Conversation
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-1">
            <ThemeToggle className="text-charcoal/70" />
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search the site"
              data-testid="mobile-search-button"
              className="p-2 text-charcoal/70"
            >
              <Search className="h-5 w-5" strokeWidth={1.75} />
            </button>
            <button
              className="p-2 text-charcoal"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-silver/30 bg-white" data-testid="mobile-nav">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-md text-base font-medium ${
                    isActive ? "bg-forest/5 text-forest" : "text-charcoal"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-3">
              <Button to="/contact" className="w-full" data-testid="mobile-header-cta">
                Start a Conversation
              </Button>
            </div>
          </div>
        </div>
      )}
      <SiteSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};
